// React access to analytics, built on the bindings bundled with posthog-js.
// This is the only file that imports `posthog-js/react`; components import
// from here.
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { PostHogCaptureOnViewed, PostHogProvider, setupReactErrorHandler, usePostHog } from "posthog-js/react";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../authTokenStore.js";
import { useCurrentUser } from "../userInfo.js";
import { buildProperties, isAnalyticsEnabled, noopClient, pauseAnalytics, reportException, resumeAnalyticsFor } from "./client.js";
import { tenantAnalyticsRegion } from "./tenantGate.js";
import { GROUP_TYPE } from "./events.js";

export function AnalyticsProvider({ client, children }) {
    return <PostHogProvider client={client ?? noopClient}>{children}</PostHogProvider>;
}

// The provider's client, or the no-op when analytics is off. Without a
// provider the bindings fall back to the global uninitialized SDK instance,
// which logs an error on every call; this hook never returns that.
export function useAnalyticsClient() {
    const client = usePostHog();
    return isAnalyticsEnabled() && client ? client : noopClient;
}

// `$element_viewed` when the wrapped surface scrolls into view. Renders the
// children untouched when analytics is off or the environment has no
// IntersectionObserver (jsdom), so dialogs never depend on the SDK.
export function CaptureOnViewed({ name, children, ...rest }) {
    if (!name || !isAnalyticsEnabled() || typeof IntersectionObserver === "undefined") return children;
    return (
        <PostHogCaptureOnViewed name={name} {...rest}>
            {children}
        </PostHogCaptureOnViewed>
    );
}

// Options for `createRoot`: every caught, uncaught and recoverable React
// error reaches PostHog with its component stack and still reaches the
// console, which React's defaults would otherwise do on their own.
export function reactRootErrorOptions(client) {
    // The bindings call `captureException` on whatever they are handed; a
    // stand-in routes it through the facade's rate limit.
    const report = client && client !== noopClient
        ? setupReactErrorHandler({ captureException: (error, properties) => reportException(client, error, properties) })
        : null;
    const handler = (error, errorInfo) => {
        console.error(error, errorInfo?.componentStack);
        report?.(error, errorInfo);
    };
    return { onCaughtError: handler, onUncaughtError: handler, onRecoverableError: handler };
}

// Identity follows the user store and the auth identity epoch. A token
// replacement or logout bumps the epoch, which resets the browser identity
// before the replacement user is identified. Mounted once, inside the
// provider, outside the router.
export function PostHogIdentity({ tenantRegion = tenantAnalyticsRegion }) {
    const client = useAnalyticsClient();
    // The provider's own client, which a pause does not swap for the no-op:
    // the reset below must clear the live SDK's identity even when the
    // previous identity left analytics paused.
    const sdk = usePostHog() ?? noopClient;
    const user = useCurrentUser();
    const epoch = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
    const lastEpoch = useRef(epoch);
    const identified = useRef(null);
    const grouped = useRef(null);
    // Bumped when analytics resumes, so the identity effect re-runs against
    // the live client.
    const [resumed, setResumed] = useState(0);

    useEffect(() => {
        if (lastEpoch.current === epoch) return;
        lastEpoch.current = epoch;
        identified.current = null;
        grouped.current = null;
        // A new identity can belong to another tenant, which may have no
        // analytics or another region: nothing is sent until that tenant's
        // region matches the project this page load started. Another region
        // stays paused until a reload starts the right project.
        pauseAnalytics();
        sdk.reset();
        // reset() clears every super property, the build's included.
        sdk.register(buildProperties());
        let current = true;
        void Promise.resolve()
            .then(() => tenantRegion())
            .then((region) => region, () => null)
            .then((region) => {
                if (!current || !resumeAnalyticsFor(region)) return;
                setResumed((n) => n + 1);
            });
        return () => {
            current = false;
        };
    }, [epoch, sdk, tenantRegion]);

    useEffect(() => {
        // Identifying while paused would mark the user done before the live
        // client learns who it is; the resume re-runs this effect.
        if (client === noopClient || !isAnalyticsEnabled()) return;
        if (typeof user?.userId !== "string" || !user.userId) return;
        const tenant = user.currentTenant ?? null;
        const key = `${epoch}:${user.userId}`;
        if (identified.current !== key) {
            const person = { is_platform_admin: Boolean(user.isPlatformAdmin) };
            if (typeof user.email === "string" && user.email) person.email = user.email;
            client.identify(user.userId, person);
            identified.current = key;
        }
        // The SDK sends $groupidentify on every group() call that carries
        // properties, and the user store refreshes every half minute, so
        // group once per tenant identity; register() below sends nothing.
        if (tenant?.tenantId) {
            const groupKey = `${epoch}:${tenant.tenantId}:${tenant.tenantName ?? ""}`;
            if (grouped.current !== groupKey) {
                client.group(GROUP_TYPE.TENANT, tenant.tenantId, tenant.tenantName ? { name: tenant.tenantName } : {});
                grouped.current = groupKey;
            }
        }
        client.register({
            tenant_id: tenant?.tenantId ?? null,
            tenant_name: tenant?.tenantName ?? null,
            tenant_role: tenant?.role ?? null,
            is_platform_admin: Boolean(user.isPlatformAdmin),
        });
    }, [user, epoch, client, resumed]);

    return null;
}
